import React, { useEffect, useState, useCallback } from 'react';
import { AppLayout } from '../components/AppLayout';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Store, XCircle, Plus, Loader2, Pencil, Check, X, Lock, Users } from 'lucide-react';

const TABS = [
  { key: 'enseignes', label: 'Enseignes', icon: Store, editable: true },
  { key: 'motifs_perte', label: 'Motifs de perte', icon: XCircle, editable: true },
  { key: 'statuts', label: 'Statuts', icon: Lock, editable: false },
  { key: 'commerciaux', label: 'Commerciaux', icon: Users, editable: false },
];

export default function ReferentielsPage() {
  const { api } = useAuth();
  const [data, setData] = useState({ enseignes: [], motifs_perte: [], statuts: [], commerciaux: [] });
  const [tab, setTab] = useState('enseignes');
  const [loading, setLoading] = useState(true);
  const [newVal, setNewVal] = useState('');
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await api().get('/referentiels');
      setData(r.data);
    } catch (e) { toast.error('Erreur de chargement des référentiels'); }
    finally { setLoading(false); }
  }, [api]);

  useEffect(() => { load(); }, [load]);

  const add = async () => {
    const v = newVal.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api().post(`/referentiels/${tab}`, { value: v });
      setNewVal('');
      await load();
      toast.success('Ajouté');
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'ajout"); }
    finally { setBusy(false); }
  };

  const saveEdit = async (id) => {
    const v = editVal.trim();
    if (!v) return;
    setBusy(true);
    try {
      await api().put(`/referentiels/${tab}/${id}`, { value: v });
      setEditId(null); setEditVal('');
      await load();
      toast.success('Modifié');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm('Supprimer cette valeur du référentiel ?')) return;
    setBusy(true);
    try {
      await api().delete(`/referentiels/${tab}/${id}`);
      await load();
      toast.success('Supprimé');
    } catch (e) { toast.error(e?.response?.data?.detail || 'Échec'); }
    finally { setBusy(false); }
  };

  const currentTab = TABS.find(t => t.key === tab);
  const items = data[tab] || [];

  return (
    <AppLayout>
      <div className="space-y-6 animate-fade-in max-w-4xl" data-testid="referentiels-page">
        <div>
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-orange-50 text-orange-600 text-xs font-semibold mb-2">
            <Store className="w-3.5 h-3.5" /> Administration
          </div>
          <h1 className="text-2xl sm:text-3xl font-heading font-bold text-slate-900 dark:text-white">Référentiels</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Listes fermées utilisées dans les formulaires et les rapports. Les statuts sont figés ; les commerciaux proviennent des utilisateurs.</p>
        </div>

        <div className="flex flex-wrap gap-2">
          {TABS.map(t => {
            const Icon = t.icon;
            return (
              <button key={t.key} onClick={() => { setTab(t.key); setEditId(null); setNewVal(''); }}
                className={`inline-flex items-center gap-2 px-4 py-2 rounded-xl text-sm font-semibold border transition-colors ${tab === t.key ? 'bg-orange-500 text-white border-orange-500' : 'bg-white dark:bg-slate-900 text-slate-600 dark:text-slate-300 border-slate-200 dark:border-slate-700 hover:border-orange-300'}`}
                data-testid={`ref-tab-${t.key}`}>
                <Icon className="w-4 h-4" /> {t.label}
              </button>
            );
          })}
        </div>

        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-5">
          {loading ? (
            <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-orange-400" /></div>
          ) : (
            <>
              {currentTab?.editable && (
                <div className="flex items-center gap-2 mb-4">
                  <input value={newVal} onChange={(e) => setNewVal(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') add(); }}
                    placeholder={`Ajouter une valeur à « ${currentTab.label} »…`}
                    className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    data-testid="ref-new-input" />
                  <button onClick={add} disabled={busy || !newVal.trim()}
                    className="inline-flex items-center gap-1 px-3 py-2 rounded-lg bg-orange-500 text-white text-sm font-semibold hover:bg-orange-600 disabled:opacity-40"
                    data-testid="ref-add-btn">
                    <Plus className="w-4 h-4" /> Ajouter
                  </button>
                </div>
              )}

              {tab === 'commerciaux' ? (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="ref-list">
                  {items.map((u) => (
                    <li key={u.id} className="flex items-center justify-between py-2.5">
                      <div>
                        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{u.name}</p>
                        <p className="text-xs text-slate-400">{u.email}</p>
                      </div>
                      <span className="text-xs px-2 py-0.5 rounded-full bg-slate-100 dark:bg-slate-800 text-slate-500 capitalize">{u.role}</span>
                    </li>
                  ))}
                  {items.length === 0 && <li className="py-6 text-center text-slate-400 text-sm">Aucun utilisateur.</li>}
                </ul>
              ) : tab === 'statuts' ? (
                <div className="flex flex-wrap gap-2" data-testid="ref-list">
                  {items.map((s) => (
                    <span key={s} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-sm">
                      <Lock className="w-3 h-3" /> {s}
                    </span>
                  ))}
                </div>
              ) : (
                <ul className="divide-y divide-slate-100 dark:divide-slate-800" data-testid="ref-list">
                  {items.map((it) => (
                    <li key={it.id} className="flex items-center justify-between py-2.5" data-testid={`ref-item-${it.id}`}>
                      {editId === it.id ? (
                        <div className="flex items-center gap-2 flex-1">
                          <input value={editVal} onChange={(e) => setEditVal(e.target.value)}
                            className="flex-1 rounded-lg border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400" autoFocus />
                          <button onClick={() => saveEdit(it.id)} className="p-1.5 rounded-lg text-emerald-600 hover:bg-emerald-50" data-testid={`ref-save-${it.id}`}><Check className="w-4 h-4" /></button>
                          <button onClick={() => { setEditId(null); setEditVal(''); }} className="p-1.5 rounded-lg text-slate-400 hover:bg-slate-100"><X className="w-4 h-4" /></button>
                        </div>
                      ) : (
                        <>
                          <span className="text-sm text-slate-800 dark:text-slate-100">{it.value}</span>
                          <div className="flex items-center gap-1">
                            <button onClick={() => { setEditId(it.id); setEditVal(it.value); }} className="p-1.5 rounded-lg text-slate-400 hover:text-orange-600 hover:bg-orange-50" data-testid={`ref-edit-${it.id}`}><Pencil className="w-4 h-4" /></button>
                            <button onClick={() => remove(it.id)} className="p-1.5 rounded-lg text-slate-400 hover:text-red-600 hover:bg-red-50" data-testid={`ref-del-${it.id}`}><XCircle className="w-4 h-4" /></button>
                          </div>
                        </>
                      )}
                    </li>
                  ))}
                  {items.length === 0 && <li className="py-6 text-center text-slate-400 text-sm">Aucune valeur. Ajoutez-en une ci-dessus.</li>}
                </ul>
              )}
            </>
          )}
        </div>
      </div>
    </AppLayout>
  );
}
